import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, Database, FileText, ListTree, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import api from '../../utils/api'
import PackageSearchSelect from './PackageSearchSelect'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getDefaultStartDate(days) {
  return addDays(formatDateText(new Date()), -(days - 1))
}

export default function UserUninstallStatsTab() {
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(getDefaultStartDate(30))
  const [endDate, setEndDate] = useState(formatDateText(new Date()))
  const [downloadSource, setDownloadSource] = useState('')
  const [viewMode, setViewMode] = useState('detail')
  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(30),
    endDate: formatDateText(new Date()),
    downloadSource: ''
  })
  const [range, setRange] = useState({
    start_date: getDefaultStartDate(30),
    end_date: formatDateText(new Date())
  })
  const [summary, setSummary] = useState({
    total_uninstalls: 0,
    source_count: 0,
    stat_day_count: 0,
    row_count: 0
  })
  const [rows, setRows] = useState([])
  const [sourceOptions, setSourceOptions] = useState([])

  const fetchData = async (nextFilters = filters) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/user-uninstall-stats', {
        params: {
          start_date: nextFilters.startDate,
          end_date: nextFilters.endDate,
          downloadSource: nextFilters.downloadSource || undefined
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setSummary(data.summary || {})
        setRows(data.rows || [])
        setRange({
          start_date: data.start_date || nextFilters.startDate,
          end_date: data.end_date || nextFilters.endDate
        })
        setSourceOptions(
          (data.source_options || []).map((item, index) => ({
            id: `source-${index}`,
            value: item,
            label: item
          }))
        )
      }
    } catch (error) {
      console.error('获取用户卸载统计失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    const nextFilters = {
      startDate,
      endDate,
      downloadSource
    }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleReset = () => {
    const nextStartDate = getDefaultStartDate(30)
    const nextEndDate = formatDateText(new Date())
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    setDownloadSource('')
    const nextFilters = {
      startDate: nextStartDate,
      endDate: nextEndDate,
      downloadSource: ''
    }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const displayRows = useMemo(() => {
    const groupedMap = new Map()
    for (const item of rows || []) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!groupedMap.has(day)) {
        groupedMap.set(day, [])
      }
      groupedMap.get(day).push(item)
    }

    return Array.from(groupedMap.entries()).flatMap(([day, items]) => {
      const sortedItems = [...items].sort((a, b) => {
        const countCompare = Number(b.uninstall_count || 0) - Number(a.uninstall_count || 0)
        if (countCompare !== 0) return countCompare
        return String(a.download_source || '').localeCompare(String(b.download_source || ''))
      })
      const totalCount = sortedItems.reduce((sum, item) => sum + Number(item.uninstall_count || 0), 0)

      return [
        ...sortedItems.map((item) => ({
          ...item,
          rowType: 'data'
        })),
        {
          stat_date: day,
          download_source: '当日合计',
          uninstall_count: totalCount,
          rowType: 'total'
        }
      ]
    })
  }, [rows])

  const summaryRows = useMemo(() => {
    const sourceMap = new Map()

    for (const item of rows || []) {
      const sourceLabel = String(item.download_source || '').trim()
      if (!sourceLabel) continue

      if (!sourceMap.has(sourceLabel)) {
        sourceMap.set(sourceLabel, {
          download_source: sourceLabel,
          uninstall_count: 0
        })
      }

      const current = sourceMap.get(sourceLabel)
      current.uninstall_count += Number(item.uninstall_count || 0)
    }

    return Array.from(sourceMap.values()).sort((a, b) => {
      const countCompare = Number(b.uninstall_count || 0) - Number(a.uninstall_count || 0)
      if (countCompare !== 0) return countCompare
      return String(a.download_source || '').localeCompare(String(b.download_source || ''))
    })
  }, [rows])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">用户卸载统计</h2>
          <p className="mt-1 text-sm text-gray-500">按日期统计各下载来源的卸载数量，未匹配渠道会拆分为“(已注册)未知用户”和“(未注册)未知用户”</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <PackageSearchSelect
            value={downloadSource}
            onChange={setDownloadSource}
            options={sourceOptions}
            placeholder="全部来源"
            searchPlaceholder="搜索下载来源"
            className="min-w-[220px]"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
          />

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>当前区间：{range.start_date} 至 {range.end_date}，下载来源 {filters.downloadSource || '全部'}</div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setViewMode('detail')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'detail'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ListTree className="h-4 w-4" />
              展开明细
            </button>
            <button
              type="button"
              onClick={() => setViewMode('summary')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'summary'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="h-4 w-4" />
              汇总列表
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">卸载总数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-red-600">
            <Trash2 className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_uninstalls || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">来源数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-blue-600">
            <BarChart3 className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.source_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">统计天数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-emerald-600">
            <CalendarDays className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.stat_day_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">统计行数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-purple-600">
            <Database className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.row_count || 0}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {viewMode === 'detail' ? '日期' : '汇总维度'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">下载来源</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">卸载数量</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : viewMode === 'detail' ? (
                displayRows.length > 0 ? (
                  displayRows.map((item, index) => (
                    <tr
                      key={`${item.stat_date}-${item.download_source}-${item.rowType}-${index}`}
                      className={item.rowType === 'total' ? 'border-t-4 border-gray-300 bg-blue-50' : 'hover:bg-gray-50'}
                    >
                      <td className={`px-6 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
                        {item.stat_date}
                      </td>
                      <td className={`px-6 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-blue-700' : 'text-gray-900'}`}>
                        {item.download_source}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-blue-700' : 'text-red-600'}`}>
                        {item.uninstall_count || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )
              ) : summaryRows.length > 0 ? (
                summaryRows.map((item, index) => (
                  <tr
                    key={`${item.download_source}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 text-sm text-gray-500">
                      区间汇总
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {item.download_source}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-red-600">
                      {item.uninstall_count || 0}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
